import { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

function getAuthorizationHeader(
  headers: InternalAxiosRequestConfig['headers'] | AxiosRequestConfig['headers'] | undefined,
): string | undefined {
  if (!headers) {
    return undefined;
  }

  if (typeof (headers as { get?: unknown }).get === 'function') {
    return (headers as { get: (name: string) => string | undefined }).get('Authorization');
  }

  return (headers as Record<string, string | undefined>)['Authorization'];
}

function setAuthorizationHeader(
  headers: InternalAxiosRequestConfig['headers'] | AxiosRequestConfig['headers'] | undefined,
  token: string,
) {
  if (!headers) {
    return;
  }

  const authValue = `Bearer ${token}`;

  if (typeof (headers as { set?: unknown }).set === 'function') {
    (headers as { set: (name: string, value: string) => void }).set('Authorization', authValue);
    return;
  }

  (headers as Record<string, string>)['Authorization'] = authValue;
}

export function injectAuthHeader<T extends InternalAxiosRequestConfig>(
  config: T,
  token: string | undefined,
): T {
  const existingAuthorizationHeader = getAuthorizationHeader(config.headers);

  if (existingAuthorizationHeader) {
    return config;
  }

  if (!token) {
    throw new Error('No Access Token Is Available');
  }

  setAuthorizationHeader(config.headers, token);

  return config;
}

export async function handleUnauthorized<T = unknown>(
  error: AxiosError<T>,
  refresh: () => Promise<string | null>,
  retry: (request: RetryableRequestConfig) => Promise<unknown>,
) {
  const prevRequest = error.config as RetryableRequestConfig | undefined;

  if (error.response?.status === 401 && prevRequest && !prevRequest._retry) {
    prevRequest._retry = true;

    const newAccessToken = await refresh();

    if (newAccessToken) {
      setAuthorizationHeader(prevRequest.headers, newAccessToken);
      return retry(prevRequest);
    }
  }

  return Promise.reject(error);
}
